import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import {
  LeadFieldConfig,
  LeadStatusConfig,
  leadFieldConfigService,
  leadStatusConfigService,
} from '../services/leadConfigService';
import { ColumnConfig } from './useTablePreferences';
import { FieldMeta } from '../config/fieldMetadata';

/**
 * The tenant's own lead fields, plus the table plumbing derived from them.
 *
 * The lead twin of usePatientFieldConfigs, minus the patient-type argument -
 * lead fields are scoped to the tenant and nothing narrower.
 */
export function useLeadFieldConfigs() {
  const { currentTenant } = useTenant();
  const [fieldConfigs, setFieldConfigs] = useState<LeadFieldConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentTenant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    leadFieldConfigService.getAll(currentTenant.id)
      .then(setFieldConfigs)
      .catch(err => console.error('Failed to load lead field configs:', err))
      .finally(() => setLoading(false));
  }, [currentTenant]);

  // Off by default, like the patient ones: they show up under Manage Columns
  // rather than widening everybody's table the moment an admin adds a field.
  const dynamicColumns: ColumnConfig[] = useMemo(() => fieldConfigs.map(config => ({
    key: `df_${config.key}`,
    label: config.label,
    defaultWidth: 130,
    minWidth: 90,
    visible: false,
    sortable: true,
    resizable: true,
    draggable: true,
  })), [fieldConfigs]);

  const dynamicFieldMeta: FieldMeta[] = useMemo(() => fieldConfigs.map(config => {
    let dataType: FieldMeta['dataType'];
    switch (config.field_type) {
      case 'number': dataType = 'number'; break;
      case 'date': dataType = 'date'; break;
      case 'bool': dataType = 'enum'; break;
      case 'single_select': dataType = 'enum'; break;
      default: dataType = 'text';
    }

    let enumOptions: { value: string; label: string }[] | undefined;
    if (config.field_type === 'bool') {
      enumOptions = [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
    } else if (config.field_type === 'single_select' && config.options) {
      enumOptions = config.options;
    }

    return { key: `df_${config.key}`, label: config.label, dataType, enumOptions };
  }), [fieldConfigs]);

  return { fieldConfigs, dynamicColumns, dynamicFieldMeta, loading };
}

/**
 * Lead statuses as the tenant defined them: the list for a filter or a picker,
 * and key → title for display.
 *
 * statusLabel falls back to the raw key rather than to blank. A blank cell in a
 * status column reads as "no status", which is a different thing from "a status
 * nobody has given a title yet".
 */
export function useLeadStatusConfigs() {
  const { currentTenant } = useTenant();
  const [statusConfigs, setStatusConfigs] = useState<LeadStatusConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentTenant) {
      setLoading(false);
      return;
    }
    setLoading(true);
    leadStatusConfigService.getAll(currentTenant.id)
      .then(setStatusConfigs)
      .catch(err => console.error('Failed to load lead status configs:', err))
      .finally(() => setLoading(false));
  }, [currentTenant]);

  const statusTitles = useMemo(() => {
    const map: Record<string, string> = {};
    statusConfigs.forEach(c => { map[c.key] = c.title; });
    return map;
  }, [statusConfigs]);

  const statusLabel = useCallback(
    (key: string | null | undefined): string => (key ? statusTitles[key] ?? key : ''),
    [statusTitles],
  );

  const defaultStatus = useMemo(
    () => statusConfigs.find(c => c.is_default)?.key ?? statusConfigs[0]?.key ?? '',
    [statusConfigs],
  );

  return { statusConfigs, statusTitles, statusLabel, defaultStatus, loading };
}
